/**
 * Consulting formatting, and the one conversion that is easy to get wrong.
 *
 * The API speaks two kinds of time and they are not interchangeable, exactly as
 * in `format.ts`: `started_at` / `ended_at` are instants stored as naive UTC,
 * and `work_date` is a plain calendar date with no instant at all. The helpers
 * here keep the two apart — `toLocalInput` / `fromLocalInput` move an instant
 * between the wire and a `datetime-local` input, while a work date is only ever
 * split on its dashes.
 *
 * Hours are printed two ways on purpose, because the invoice prints them two
 * ways: the summary trims trailing zeros (19.5, 38) and the detail pads to two
 * places (8.50, 2.00). Matching that here is what makes the screen and the
 * document agree at a glance.
 */

import type { RateKind, WorkDay } from '@/api/types'

/** The billing granularity. Every stored stretch of work is a multiple of it. */
export const QUARTER_HOUR = 0.25

/** "8.50" — how the detail table writes hours. */
export function formatHours(value: number): string {
  return value.toFixed(2)
}

/** "19.5", "38" — how the summary table writes them. */
export function trimHours(value: number): string {
  return String(Number(value.toFixed(2)))
}

export function formatMoney(value: number): string {
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** "$250.00", or "Joint Venture" / "JV" — a rate, not the absence of one. */
export function rateLabel(kind: RateKind, rate: number | null, short = false): string {
  if (kind === 'joint_venture' || rate === null) return short ? 'JV' : 'Joint Venture'
  return formatMoney(rate)
}

/** "8/31/2026", the form the invoice's Date column uses. */
export function formatWorkDate(workDate: string): string {
  const [year, month, day] = workDate.split('-').map(Number)
  return `${month}/${day}/${year}`
}

/** "Mon 31 Aug", for the screen rather than the document. */
export function formatWorkDateLong(workDate: string): string {
  const [year, month, day] = workDate.split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** Today as YYYY-MM-DD in the browser's zone. */
export function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** The first of the current month, for the default filter range. */
export function monthStartIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`
}

/**
 * A naive-UTC instant from the API as a `datetime-local` input value.
 *
 * The trailing Z is what makes this correct: without it the browser reads the
 * string as local time and the field shows an hour that is seven off in Denver.
 */
export function toLocalInput(value: string): string {
  const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`
  const instant = new Date(withZone)
  return (
    `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}` +
    `T${pad(instant.getHours())}:${pad(instant.getMinutes())}`
  )
}

/**
 * A `datetime-local` value back to an instant for the API.
 *
 * Sent as a real offset-aware ISO string rather than the bare local text, so
 * the server never has to guess which 9:00 was meant.
 */
export function fromLocalInput(value: string): string {
  return new Date(value).toISOString()
}

/** "9:05 AM" for a segment's start and stop. */
export function formatClock(value: string): string {
  const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`
  return new Date(withZone).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "1:23:45" — the running timer, counting up from its start. */
export function elapsedLabel(startedAt: string, now: number): string {
  const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(startedAt) ? startedAt : `${startedAt}Z`
  const seconds = Math.max(0, Math.floor((now - new Date(withZone).getTime()) / 1000))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}:${pad(minutes)}:${pad(seconds % 60)}`
}

/**
 * What a running stretch will bill if it is stopped now.
 *
 * Rounds up to the next quarter hour, matching the server exactly — including
 * the part that is not obvious: a stretch already on a quarter boundary stays
 * there rather than jumping to the next one.
 */
export function billableSoFar(startedAt: string, now: number): number {
  const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(startedAt) ? startedAt : `${startedAt}Z`
  const minutes = (now - new Date(withZone).getTime()) / 60000
  if (minutes <= 0) return 0
  return Math.ceil(minutes / 15) * QUARTER_HOUR
}

/** The rows the user has ticked, in the order they are displayed. */
export function selectedDays(days: WorkDay[], selection: ReadonlySet<number>): WorkDay[] {
  return days.filter((day) => selection.has(day.id))
}
